import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import BiosenseSignalMonitor from './BiosenseSignalMonitor';
import SettingsBars from './SettingsBars';
import DemographicsForm, { UserDemographics } from './DemographicsForm';
import ReportPage from './ReportPage';
import WelcomePage from './WelcomePage';
import Logo from './Logo';
import FooterLogo from './FooterLogo';
import { Flex } from './shared/Flex';
import { useCameras, useDisableZoom, useResultsExport } from '../hooks';
import { MeasurementResults } from '../hooks/useResultsExport';
import { useReportDelivery } from '../hooks/useReportDelivery';
import MetricDetailPage from './health/MetricDetailPage';
import CeIndicatorDetailPage from './health/CeIndicatorDetailPage';
import ReportPdfView from './health/ReportPdfView';
import InstructionsPage from './InstructionsPage';
import { MetricId } from './health/metricConfig';
import JourneyOnboarding from './health/JourneyOnboarding';
import CheckInStep from './health/CheckInStep';
import { devJourneyProfile } from './health/glp1DevProfile';
import { JOURNEY_TITLE_KEY, CHECK_IN_KEYS } from './health/glp1Copy';
import { TherapyProfile, CheckIn } from '../types/glp1';
import { colors, fontSize, fontWeight, spacing } from '../style/tokens';
import { headerBg, headerFg, headerHoverBg } from '../style/header';
import { BrandingContext } from './shared/BrandingContext';
import { ensureMonitorInitialized } from '../sdk/monitorInit';
import CONFIG from 'config';
import { generatePdf } from '../utils/generatePdf';
import { reportFileName } from '../utils/reportFileName';
import { ceCertificateUrl } from '../utils/ceCertificateUrl';
import checkCeLabel from '../assets/images/webapp_CHECK_CE_label.png';
import { fetchVitalSignsContent, VitalSignsContentMap } from '../api/vitalSignsContent';
import { Sex, SmokingStatus, ConfidenceLevel } from '@biosensesignal/web-sdk';
import MobileRequired from './MobileRequired';
import SessionEnded from './SessionEnded';
import InvalidVoucher from './InvalidVoucher';

const IS_DEV = process.env.NODE_ENV !== 'production';

const Container = styled(Flex)<{ isSettingsOpen: boolean }>`
  height: 100%;
  width: 100%;
  position: relative;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  background-color: ${({ isSettingsOpen }) => isSettingsOpen && 'rgba(0, 0, 0, 0.5)'};
`;

const DemographicsPage = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: var(--sfondo, ${colors.bg});
  overflow-y: auto;
  font-family: 'Outfit', sans-serif;
`;

const DemographicsTopBar = styled.header`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px 26px;
  background: ${headerBg};
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
  position: relative;
`;

const LogoCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
`;

// Stands in for BackButton when there's nowhere to go back. Needs the full
// height as well as the width: the logo is absolutely positioned, so the
// button is what gives the top bar its height.
const BackSpacer = styled.span`
  width: 42px;
  height: 42px;
  flex-shrink: 0;
`;

const BackButton = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1.5px solid ${colors.border};
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s;
  @media (hover: hover) { &:hover { background: ${headerHoverBg}; } }
  &::before {
    content: '';
    display: block;
    width: 9px;
    height: 9px;
    border-top: 2px solid ${headerFg};
    border-left: 2px solid ${headerFg};
    transform: rotate(-45deg) translate(2px, 0);
  }
`;

const DemographicsContent = styled.div`
  flex: 1;
  padding: 0 14px;
`;

const FormTitleBlock = styled.div`
  background: transparent;
  padding: 28px 24px 22px;
  margin-bottom: 10px;
`;

const PageTitle = styled.h1`
  font-size: clamp(26px, 7vw, 32px);
  font-weight: ${fontWeight.extrabold};
  color: ${colors.text};
  line-height: 1.2;
  margin-bottom: 10px;
`;

const PageSubtitle = styled.p`
  font-size: ${fontSize.md};
  color: ${colors.muted};
  line-height: 1.6;
  margin-top: ${spacing.sm};
`;

// Shown while the report is being handed to a partner's endpoint on a flow that
// has no report page of its own — otherwise the user stares at the finished scan
// screen for up to the backend's 10 s partner timeout before being redirected.
const DeliveryOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--sfondo, ${colors.bg});
  font-family: 'Outfit', sans-serif;
  font-size: ${fontSize.lg};
  color: ${colors.text};
  text-align: center;
  padding: 0 24px;
`;

const REPORT_SESSION_KEY = 'comestai_report_data';

const getInitialReportData = (): MeasurementResults | null => {
  try {
    const saved = sessionStorage.getItem(REPORT_SESSION_KEY);
    return saved ? JSON.parse(saved) as MeasurementResults : null;
  } catch {
    return null;
  }
};

const App = () => {
  const intl = useIntl();
  const { tokenDemographics, branding, isLoadingToken, sessionExpired, invalidVoucher } = React.useContext(BrandingContext);
  // The demographics page is forced on when the JWT carried no usable biometric
  // data, even if the partner's branding turns it off: the scores need sex/age/
  // weight/height, and without the form there is no other way to collect them.
  const isPageEnabled = useCallback((key: string) =>
    (key === 'informazioni' && !tokenDemographics)
    || !branding.pages || branding.pages.includes(key),
  [branding.pages, tokenDemographics]);
  // Signed delivery of the report to the partner's own endpoint. `returnUrl` is
  // their per-session results page, used as the report CTA; when the report page
  // is disabled the hook redirects there instead.
  const {
    deliver: deliverReport,
    returnUrl: reportReturnUrl,
    isDelivering: isDeliveringReport,
  } = useReportDelivery(isPageEnabled('report'));
  const cameras = useCameras();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [cameraId, setCameraId] = useState<string>();
  const [isLicenseValid, setIsLicenseValid] = useState(false);
  const [userDemographics, setUserDemographics] = useState<UserDemographics | null>(null);

  const [showWelcomePage, setShowWelcomePage] = useState(() => getInitialReportData() === null);
  const [showDemographicsForm, setShowDemographicsForm] = useState(false);
  const [showDemographicsPage, setShowDemographicsPage] = useState(false);
  const [showInstructionsPage, setShowInstructionsPage] = useState(false);
  // GLP-1 journey context. Held in React state only: the app keeps nothing
  // across sessions (§3), and these are the user's own confirmed values for
  // this scan. Null profile = skipped or not on therapy → no Journey panel.
  const [showJourneyPage, setShowJourneyPage] = useState(false);
  const [showCheckInPage, setShowCheckInPage] = useState(false);
  const [journeyProfile, setJourneyProfile] = useState<TherapyProfile | null>(null);
  const [journeyCheckIn, setJourneyCheckIn] = useState<CheckIn | null>(null);
  const [severeSymptom, setSevereSymptom] = useState(false);

  const [showReportPage, setShowReportPage] = useState(() => getInitialReportData() !== null);
  const [reportData, setReportData] = useState<MeasurementResults | null>(getInitialReportData);
  const [shouldStartMeasurement, setShouldStartMeasurement] = useState(false);
  const [selectedMetricId, setSelectedMetricId] = useState<MetricId | null>(null);
  // CE report: resultCode of the indicator whose detail page is open (null = report).
  const [selectedCeCode, setSelectedCeCode] = useState<string | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  // CE only: vital-signs content map pre-fetched before PDF capture so the
  // off-screen ReportPdfView can render descriptions synchronously.
  const [pdfContentMap, setPdfContentMap] = useState<VitalSignsContentMap | null>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  useDisableZoom();

  // Pre-warm the BioSense SDK (WASM download + license validation) as soon as the
  // license key is known — while the user is still on the welcome / demographics /
  // instructions pages. This moves that work off the critical path so it no longer
  // blocks the camera open (createFaceSession) the instant the scan screen mounts.
  // Memoized in monitorInit, so this is safe to call before useMonitor also does.
  const prewarmLicenseKey = branding.licenseKey ?? CONFIG.biosenseLicenseKey;
  useEffect(() => {
    if (prewarmLicenseKey) {
      // Errors are surfaced later by useMonitor when the monitor mounts.
      ensureMonitorInitialized(prewarmLicenseKey).catch(() => undefined);
    }
  }, [prewarmLicenseKey]);

  // const { downloadLastResults } = useResultsExport(
  //   userDemographics,
  //   60, // default processing time
  // );

  const onSettingsClickedHandler = useCallback((event) => {
    const settingsBars = document.getElementById('settingsBars');
    const isSettingsButtonClicked = event.target.id === 'settingsButton';

    const isInsideSettingsClicked =
      (settingsBars?.contains(event.target as Node) ?? false) || isSettingsButtonClicked;

    if (!isInsideSettingsClicked) {
      setIsSettingsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', onSettingsClickedHandler);
    return () => {
      document.removeEventListener('click', onSettingsClickedHandler);
    };
  }, []);

  const updateLicenseStatus = useCallback((valid) => {
    setIsLicenseValid(valid);
  }, []);

  const toggleSettingsClick = useCallback(() => {
    setIsSettingsOpen(!isSettingsOpen);
  }, [isSettingsOpen]);

  const handleCloseSettings = useCallback(({ cameraId }) => {
    setCameraId(cameraId);
    setIsSettingsOpen(false);
  }, []);

  useEffect(() => {
    if (!cameras?.length) return;
    setCameraId(cameras[0].deviceId);
  }, [cameras]);

  // Once branding loads, skip to the first enabled page if welcome is disabled.
  // useLayoutEffect (not useEffect) so the redirect runs before the browser
  // paints — otherwise the disabled welcome page flashes for one frame.
  useLayoutEffect(() => {
    if (isLoadingToken) return;
    if (isPageEnabled('benvenuto')) return;
    setShowWelcomePage(false);
    if (isPageEnabled('informazioni')) setShowDemographicsPage(true);
    // Mirrors goPastDemographics; inlined because this effect deliberately
    // depends on isLoadingToken alone, and branding has resolved by the time it
    // runs. Keep the two in step.
    else if (branding.istanza_glp1) setShowJourneyPage(true);
    else if (isPageEnabled('istruzioni')) setShowInstructionsPage(true);
    // else fall through to facescan
  }, [isLoadingToken]);

  // After demographics, a GLP-1 instance inserts the two journey screens before
  // instructions: the skippable "Your journey" step (§7), then the 60-second
  // check-in (§4.12). Both are gated on the tenant flag alone here — the
  // per-user half of glp1Active is what the first screen collects.
  const journeyEnabled = branding.istanza_glp1;

  const afterJourneySteps = useCallback(() => {
    if (isPageEnabled('istruzioni')) setShowInstructionsPage(true);
    // else fall through to facescan
  }, [isPageEnabled]);

  /**
   * Leaves the demographics stage behind — every route that does so must come
   * through here. A partner with the demographics page disabled skips straight
   * past it, and routing the journey step only off the form's Save would make
   * it unreachable for them.
   */
  const goPastDemographics = useCallback(() => {
    if (journeyEnabled) setShowJourneyPage(true);
    else afterJourneySteps();
  }, [journeyEnabled, afterJourneySteps]);

  const handleDemographicsSave = useCallback((demographics: UserDemographics) => {
    setUserDemographics(demographics);
    setShowDemographicsForm(false);
    setShowDemographicsPage(false);
    goPastDemographics();
  }, [goPastDemographics]);

  const handleJourneySave = useCallback((profile: TherapyProfile) => {
    setJourneyProfile(profile);
    setShowJourneyPage(false);
    // Only someone on therapy has a tolerability check-in to give.
    if (profile.therapyStatus === 'glp1') setShowCheckInPage(true);
    else afterJourneySteps();
  }, [afterJourneySteps]);

  // Skipping leaves the profile unconfirmed, so it is never used: the Journey
  // panel then shows UNAVAILABLE rather than values the user never saw. That
  // reading satisfies both §7 ("fully skippable") and ruling B5 ("explicit
  // confirmation before first use").
  const handleJourneySkip = useCallback(() => {
    setJourneyProfile(null);
    setShowJourneyPage(false);
    afterJourneySteps();
  }, [afterJourneySteps]);

  const handleCheckInSave = useCallback((checkIn: CheckIn, severe: boolean) => {
    setJourneyCheckIn(checkIn);
    setSevereSymptom(severe);
    setShowCheckInPage(false);
    afterJourneySteps();
  }, [afterJourneySteps]);

  const handleCheckInSkip = useCallback(() => {
    setJourneyCheckIn(null);
    setSevereSymptom(false);
    setShowCheckInPage(false);
    afterJourneySteps();
  }, [afterJourneySteps]);

  const handleStartMonitoring = useCallback(() => {
    setShowDemographicsForm(true);
  }, []);

  const handleShowReport = useCallback((results: MeasurementResults) => {
    sessionStorage.setItem(REPORT_SESSION_KEY, JSON.stringify(results));
    setReportData(results);
    if (isPageEnabled('report')) setShowReportPage(true);
    // Hand the report to the partner's endpoint when they have one configured.
    // Fire-and-forget: the report page is already up, and a slow or broken
    // partner endpoint must not hold the user's results hostage. When the
    // report page is disabled, deliverReport redirects on success instead.
    deliverReport(results);
  }, [isPageEnabled, deliverReport]);

  const handleMeasureAgain = useCallback(() => {
    sessionStorage.removeItem(REPORT_SESSION_KEY);
    setShowReportPage(false);
    setSelectedMetricId(null);
    setSelectedCeCode(null);
    setShouldStartMeasurement(true);
  }, []);

  const handleMetricClick = useCallback((id: MetricId) => {
    setSelectedMetricId(id);
  }, []);

  const handleBackFromMetric = useCallback(() => {
    setSelectedMetricId(null);
  }, []);

  const handleCeIndicatorClick = useCallback((code: string) => {
    setSelectedCeCode(code);
  }, []);

  const handleBackFromCeIndicator = useCallback(() => {
    setSelectedCeCode(null);
  }, []);

  const handleContinueFromWelcome = useCallback(() => {
    setShowWelcomePage(false);
    if (isPageEnabled('informazioni')) setShowDemographicsPage(true);
    else goPastDemographics();
  }, [isPageEnabled, goPastDemographics]);

  // Each pre-scan page only gets a back button when an enabled page sits before
  // it. Hiding the page alone would drop the user onto the facescan, which is
  // what renders when every other show* flag is off.
  const canGoBackFromDemographics = isPageEnabled('benvenuto');
  const canGoBackFromJourney = isPageEnabled('informazioni') || isPageEnabled('benvenuto');
  const canGoBackFromInstructions = isPageEnabled('informazioni') || isPageEnabled('benvenuto');

  const handleBackFromDemographics = useCallback(() => {
    setShowDemographicsPage(false);
    setShowWelcomePage(true);
  }, []);

  const handleBackFromJourney = useCallback(() => {
    setShowJourneyPage(false);
    if (isPageEnabled('informazioni')) setShowDemographicsPage(true);
    else setShowWelcomePage(true);
  }, [isPageEnabled]);

  const handleBackFromInstructions = useCallback(() => {
    setShowInstructionsPage(false);
    if (isPageEnabled('informazioni')) setShowDemographicsPage(true);
    else setShowWelcomePage(true);
  }, [isPageEnabled]);

  const handleNextFromInstructions = useCallback(() => {
    setShowInstructionsPage(false);
  }, []);

  const handlePreviewReport = useCallback(() => {
    // Dev-only preview: randomise the score-driving inputs each time so the
    // indicators land across different bands (red/yellow/green) instead of all
    // clustering in one range. Ranges are wide but physiologically plausible.
    const rand = (min: number, max: number) => Math.round(min + Math.random() * (max - min));
    // Categorical zone/risk enums (1 = LOW, 2 = NORMAL/MEDIUM, 3 = HIGH).
    const level = () => rand(1, 3);
    const sex = Math.random() < 0.5 ? Sex.MALE : Sex.FEMALE;
    const age = rand(25, 70);
    const height = rand(160, 190);
    const weight = rand(52, 105); // → BMI ≈ 16–39 depending on height
    const smokingStatus = Math.random() < 0.4 ? SmokingStatus.SMOKER : SmokingStatus.NON_SMOKER;
    const pulseRate = rand(48, 104);
    const rmssd = rand(12, 70);

    setReportData({
      timestamp: '2026-05-20_13-00-27',
      sessionInfo: {
        measurementDate: '20/05/2026',
        measurementTime: '13:00:27',
        duration: 60,
      },
      demographics: {
        sex,
        age,
        weight,
        height,
        smokingStatus,
      },
      vitalSigns: {
        pulseRate,
        // v2.0 gate (G1/G2): HR- and RMSSD-derived indicators need MEDIUM+
        // confidence on pulseRate / sdnn, else they render "Not available".
        // Fake report → HIGH so the layout shows fully-populated indicators.
        pulseRateConfidenceLevel: ConfidenceLevel.LOW,
        sdnnConfidenceLevel: ConfidenceLevel.LOW,
        respirationRate: 16,
        stressLevel: rand(1, 5), // StressLevel enum: 1=LOW … 5=EXTREME
        sdnn: 62,
        hemoglobin: 14.8,
        bloodPressure: { systolic: 118, diastolic: 76 },
        rmssd,
        meanRri: 833,
        sd1: 27,
        sd2: 52,
        prq: 4.2,
        pnsIndex: 0.6,
        snsIndex: 0.4,
        lfhf: 1.2,
        wellnessIndex: 72,
        wellnessLevel: 3,
        stressIndex: 24,
        normalizedStressIndex: 0.28,
        hemoglobinA1c: 5.4,
        ascvdRisk: rand(0, 35), // continuous 10-yr % → spans low/intermediate/high
        heartAge: 43,
        cardiacWorkload: 8200,
        meanArterialPressure: 90,
        pulsePressure: 42,
        oxygenSaturation: 98,
        pnsZone: level(),
        snsZone: level(),
        ascvdRiskLevel: level(),
        highBloodPressureRisk: level(),
        highFastingGlucoseRisk: level(),
        highHemoglobinA1CRisk: level(),
        highTotalCholesterolRisk: level(),
        lowHemoglobinRisk: level(),
      },
    });
    setShowWelcomePage(false);
    setShowReportPage(true);
  }, []);

  const handleDownloadReport = useCallback(async () => {
    if (!reportData || isGeneratingPdf) return;
    // CE PDF includes per-indicator descriptions from the content API. Warm the
    // (locale-cached) map first so the off-screen PDF view renders it in one
    // synchronous pass; on failure fall back to bars-only rather than blocking.
    if (branding.istanza_ce) {
      try {
        setPdfContentMap(await fetchVitalSignsContent(intl.locale));
      } catch {
        setPdfContentMap(null);
      }
    }
    setIsGeneratingPdf(true);
  }, [reportData, isGeneratingPdf, branding.istanza_ce, intl.locale]);

  useEffect(() => {
    if (!isGeneratingPdf || !pdfContainerRef.current || !reportData) return;
    const el = pdfContainerRef.current;
    const filename = reportFileName(branding.istanza_ce, reportData.timestamp, 'pdf');
    // CE only: partner logo top-left, and a disclaimer above the CE label,
    // bottom-right, on every PDF page.
    const logo = branding.istanza_ce ? branding.logo : null;
    const footer = branding.istanza_ce ? checkCeLabel : null;
    const disclaimer = branding.istanza_ce
      ? { text: intl.formatMessage({ id: 'ce.footerDisclaimer' }), rtl: intl.locale === 'ar' }
      : null;
    // The CE label links to the per-language CE certificate page.
    const footerLink = branding.istanza_ce ? ceCertificateUrl(intl.locale) : null;
    setTimeout(async () => {
      try {
        await generatePdf(el, filename, { logo, footer, disclaimer, footerLink });
      } finally {
        setIsGeneratingPdf(false);
      }
    }, 150);
  }, [isGeneratingPdf]);

  const effectiveDemographics = userDemographics ?? tokenDemographics;

  if (!IS_DEV && !(window as any).__isMobileDevice) {
    return <MobileRequired />;
  }

  // Don't render any page until branding has loaded, otherwise the default
  // welcome page flashes before the redirect effect can skip it when it's
  // not an enabled page. ThemedBackground (in index.tsx) keeps the brand
  // background visible during this brief load.
  if (isLoadingToken) {
    return null;
  }

  // Voucher rejected by the backend balance check (invalid/unknown token or
  // voucher) — hard block.
  if (invalidVoucher) {
    return <InvalidVoucher />;
  }

  // Token expired with no redirect target — show the session-ended screen.
  if (sessionExpired) {
    return <SessionEnded />;
  }

  const showMonitorPage = !showWelcomePage && !showDemographicsPage && !showInstructionsPage
    && !showJourneyPage && !showCheckInPage && !showReportPage;

  return (
    <>
      {isDeliveringReport && !isPageEnabled('report') && (
        <DeliveryOverlay>{intl.formatMessage({ id: 'report.sendingResults' })}</DeliveryOverlay>
      )}

      {showMonitorPage && <Container isSettingsOpen={isSettingsOpen}>
        <BiosenseSignalMonitor
          showMonitor={!isSettingsOpen}
          cameraId={cameraId ?? ''}
          onLicenseStatus={updateLicenseStatus}
          onSettingsClick={toggleSettingsClick}
          onBack={
            isPageEnabled('istruzioni') || isPageEnabled('informazioni') || isPageEnabled('benvenuto')
              ? () => {
                  if (isPageEnabled('istruzioni')) setShowInstructionsPage(true);
                  else if (isPageEnabled('informazioni')) setShowDemographicsPage(true);
                  else if (isPageEnabled('benvenuto')) setShowWelcomePage(true);
                }
              : undefined
          }
          isSettingsOpen={isSettingsOpen}
          onShowReport={handleShowReport}
          shouldStartMeasurement={shouldStartMeasurement}
          onMeasurementStarted={() => setShouldStartMeasurement(false)}
          userDemographics={effectiveDemographics}
          onPreviewReport={handlePreviewReport}
        />
        <SettingsBars
          open={isSettingsOpen}
          onClose={handleCloseSettings}
          cameras={cameras}
          isLicenseValid={isLicenseValid}
        />
      </Container>}

      {showWelcomePage && (
        <WelcomePage onContinue={handleContinueFromWelcome} onPreviewReport={handlePreviewReport} />
      )}

      {showDemographicsPage && (
        <DemographicsPage>
          <DemographicsTopBar>
            {canGoBackFromDemographics
              ? <BackButton onClick={handleBackFromDemographics} aria-label="Back" />
              : <BackSpacer />}
            <LogoCenter><Logo /></LogoCenter>
            <span style={{ width: 42 }} />
          </DemographicsTopBar>
          <FormTitleBlock>
            <PageTitle>{intl.formatMessage({ id: 'demographics.title' })}</PageTitle>
            <PageSubtitle>
              {intl.formatMessage({ id: 'welcome.message' })}
            </PageSubtitle>
          </FormTitleBlock>
          <DemographicsContent>
            <DemographicsForm
              isOpen={true}
              onSave={handleDemographicsSave}
              onCancel={() => { }}
              initialData={tokenDemographics || userDemographics || undefined}
            />
          </DemographicsContent>
          <FooterLogo />
        </DemographicsPage>
      )}

      {showJourneyPage && (
        <DemographicsPage>
          <DemographicsTopBar>
            {canGoBackFromJourney
              ? <BackButton onClick={handleBackFromJourney} aria-label="Back" />
              : <BackSpacer />}
            <LogoCenter><Logo /></LogoCenter>
            <span style={{ width: 42 }} />
          </DemographicsTopBar>
          <FormTitleBlock>
            <PageTitle>{intl.formatMessage({ id: JOURNEY_TITLE_KEY })}</PageTitle>
          </FormTitleBlock>
          <DemographicsContent>
            <JourneyOnboarding
              // Dev scaffold standing in for partner-supplied token values, so
              // the review-and-confirm path (ruling B5) can be exercised.
              initial={devJourneyProfile()}
              onSave={handleJourneySave}
              onSkip={handleJourneySkip}
            />
          </DemographicsContent>
          <FooterLogo />
        </DemographicsPage>
      )}

      {showCheckInPage && (
        <DemographicsPage>
          <DemographicsTopBar>
            <BackButton onClick={() => { setShowCheckInPage(false); setShowJourneyPage(true); }} aria-label="Back" />
            <LogoCenter><Logo /></LogoCenter>
            <span style={{ width: 42 }} />
          </DemographicsTopBar>
          <FormTitleBlock>
            <PageTitle>{intl.formatMessage({ id: CHECK_IN_KEYS.title })}</PageTitle>
          </FormTitleBlock>
          <DemographicsContent>
            <CheckInStep onSave={handleCheckInSave} onSkip={handleCheckInSkip} />
          </DemographicsContent>
          <FooterLogo />
        </DemographicsPage>
      )}

      {showInstructionsPage && (
        <InstructionsPage
          onBack={canGoBackFromInstructions ? handleBackFromInstructions : undefined}
          onNext={handleNextFromInstructions}
        />
      )}

      {showReportPage && selectedMetricId && reportData && (
        <Container isSettingsOpen={false}>
          <MetricDetailPage
            metricId={selectedMetricId}
            results={reportData}
            onBack={handleBackFromMetric}
          />
        </Container>
      )}

      {showReportPage && !selectedMetricId && selectedCeCode && reportData && (
        <Container isSettingsOpen={false}>
          <CeIndicatorDetailPage
            code={selectedCeCode}
            results={reportData}
            onBack={handleBackFromCeIndicator}
          />
        </Container>
      )}

      {showReportPage && !((selectedMetricId || selectedCeCode) && reportData) && (
        <Container isSettingsOpen={isSettingsOpen}>
          <ReportPage
            results={reportData}
            onMeasureAgain={handleMeasureAgain}
            onDownload={handleDownloadReport}
            isDownloading={isGeneratingPdf}
            onSettingsClick={toggleSettingsClick}
            onMetricClick={handleMetricClick}
            onCeIndicatorClick={handleCeIndicatorClick}
            dataReturnUrl={reportReturnUrl}
            journeyProfile={journeyProfile}
            journeyCheckIn={journeyCheckIn}
            severeSymptom={severeSymptom}
          />
          <SettingsBars
            open={isSettingsOpen}
            onClose={handleCloseSettings}
            cameras={cameras}
            isLicenseValid={isLicenseValid}
          />
          {isGeneratingPdf && reportData && (
            <div
              ref={pdfContainerRef}
              style={{ position: 'absolute', left: '-9999px', top: 0, zIndex: -1 }}
            >
              <ReportPdfView results={reportData} contentMap={pdfContentMap} />
            </div>
          )}
        </Container>
      )}
    </>
  );
};

export default App;
